import { useEffect, useRef, useState } from 'react';

declare const window: Window & {
  ConsentSDK: any;
};

interface ConsentWidgetProps {
  country?: string;
  role?: string;
  locale?: string;
  onReady?: (form: ConsentForm) => void;
  onChange?: (values: Record<string, boolean>) => void;
}

export interface ConsentForm {
  getSubmissionPayload: () => Promise<any>;
}

const ENLIGHTEN_URL = process.env.REACT_APP_ENLIGHTEN_URL || '';
export const GLOBAL_BACKEND_API =
  process.env.REACT_APP_GLOBAL_BACKEND_API || '';

export default function ConsentWidget({
  country = 'US',
  role = 'HOMEOWNER',
  locale = 'en',
  onReady,
  onChange,
}: ConsentWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const sdkRef = useRef<any>(null);
  const onReadyRef = useRef(onReady);
  const onChangeRef = useRef(onChange);
  const [isSDKLoaded, setIsSDKLoaded] = useState(
    typeof window.ConsentSDK !== 'undefined'
  );

  onReadyRef.current = onReady;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (isSDKLoaded) return;

    const interval = setInterval(() => {
      if (typeof window.ConsentSDK !== 'undefined') {
        setIsSDKLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isSDKLoaded]);

  useEffect(() => {
    if (!isSDKLoaded || !containerRef.current) return;

    const sdk = new window.ConsentSDK({
      baseUrl: ENLIGHTEN_URL,
    });
    sdkRef.current = sdk;

    sdk.clearCache();

    sdk.onChange(
      ({
        allValues,
      }: {
        consentId: string;
        value: boolean;
        allValues: Record<string, boolean>;
      }) => {
        onChangeRef.current?.(allValues);
      }
    );

    sdk
      .fetchConsents({ country, role, locale, formType: 'LEAD' })
      .then((data: any) => {
        const form: ConsentForm = sdk.renderHTML(
          data,
          null,
          containerRef.current,
          footerRef.current
        );
        onReadyRef.current?.(form);
      })
      .catch((error: any) => {});

    return () => {
      sdk.clearCache();
    };
  }, [isSDKLoaded, country, role, locale]);

  if (!ENLIGHTEN_URL || !GLOBAL_BACKEND_API) {
    console.warn(
      'ConsentWidget: REACT_APP_ENLIGHTEN_URL or REACT_APP_GLOBAL_BACKEND_API is not set.'
    );
    return null;
  }

  return (
    <div className="consent-widget text-[11px] md:text-xs">
      <div ref={containerRef} />
      <div ref={footerRef} />
    </div>
  );
}
